
import React, { useState, useEffect } from "react";
import axiosInstance from '../axiosConfig';
import Header from "../Components/Header";
import Sidebar from "../Components/Sidebar";
import '../styles.css';

const UserProfile = () => {
  const [activeSection, setActiveSection] = useState('official');
  const [userData, setUserData] = useState(null);
  const [officeBranch, setOfficeBranch] = useState(null);
  const currentUserId = localStorage.getItem('id'); 
  const [isSidebarVisible, setIsSidebarVisible] = useState(false);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [message, setMessage] = useState('');
  const [isContentBlurred, setIsContentBlurred] = useState(false);
  const [isIdCardPopupVisible, setIsIdCardPopupVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(null);
  const [error, setError] = useState(null);
  const [roleError, setRoleError] = useState('');
  const [rolesData, setRolesData] = useState([]);

  useEffect(() => {
    const screenWidth = window.innerWidth;
    setIsSidebarVisible(screenWidth >= 520);
  }, []);

  const handleToggleClick = () => setIsSidebarVisible(!isSidebarVisible);

  useEffect(() => {
    axiosInstance.get(`/users/${currentUserId}/roles/`)
      .then(res => {
        setRolesData([res.data.active_role, res.data.assigned_role]);
      })
      .catch(console.error);
  }, []);

  const handleSwitchRole = async () => {
    try {
      await axiosInstance.post('/switch-role/', { role_id: rolesData[1].id });
      window.location.href = '/admin-manager-lead-dashboard';
    } catch (err) {
      setRoleError(err.response?.data?.detail || "Failed to switch role");
    }
  };

  const handleQueryClick = () => {
    setIsPopupVisible(true);
    setIsContentBlurred(true);
  };

  const handleSendMessage = () => {
    if (!message.trim()) {
      setError('Please enter a message');
      return;
    }
    setIsLoading(true);
    setError(null);
    setSuccess(null);
    
    const queryData = {
      query: message,
    };

    axiosInstance.post('/queries/', queryData)
      .then((response) => {
        if (response.status === 201) {
          setSuccess('Query sent successfully!');
          setTimeout(() => {
            setIsPopupVisible(false);
            setIsContentBlurred(false);
            setMessage('');
            setSuccess(null);
          }, 1500);
        }
      })
      .catch((err) => {
        console.error(err);
        setError('Error sending query. Please try again.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handlePopupClose = () => {
    setIsPopupVisible(false);
    setIsContentBlurred(false);
    setMessage('');
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    if (currentUserId) {
      axiosInstance.get(`/user/${currentUserId}/`)
        .then((response) => {
          setUserData(response.data);
          axiosInstance.get(`/office-branch/${response.data.work_location}/`)
            .then((response) => setOfficeBranch(response.data.name))
            .catch((error) => console.error('Error fetching office branch:', error));
        })
        .catch((error) => console.error('Error fetching user data:', error));
    }
  }, [currentUserId]);

  const handleSectionChange = (event) => {
    setActiveSection(event.target.value);
  };

  const handleLogout = () => {
    localStorage.clear();
    window.location.href = '/';
  };

  const profilePhotoUrl = userData?.profile_photo?.startsWith('http://') 
    ? userData?.profile_photo.replace('http://', 'https://') 
    : userData?.profile_photo;

  const menuItems = [
    { icon: '&#127968;', label: 'Dashboard', path: '/user-dashboard' },
    { icon: '&#128100;', label: 'My Profile', path: `/user-profile/${userData?.id}` },
    { icon: '&#128197;', label: 'Attendance', path: `/leave-attendance/${userData?.id}` },
    { icon: '&#128203;', label: 'Leave Management', path: `/leave-management/${userData?.id}` },
    // { icon: '&#128197;', label: 'Calendar', path: '/calendar', condition: userData?.role_name === 'employee' },
    // { icon: '&#128178;', label: 'Payroll', path: 'https://hrms.teamhalt.com/', active: false },
    { icon: '&#128179;', label: 'ID Card', action: () => setIsIdCardPopupVisible(true) },
  ];

  const DetailRow = ({ label, value }) => (
    <div className="profile-detail-row">
      <span className="profile-detail-label">{label}</span>
      <span className="profile-detail-value">{value || 'N/A'}</span>
    </div>
  );

  return (
    <div className={`dashboard-wrapper ${isSidebarVisible ? 'sidebar-open' : 'sidebar-closed'}`}>
      <Header onToggleClick={handleToggleClick} isSidebarVisible={isSidebarVisible} />
      <Sidebar 
        isSidebarVisible={isSidebarVisible} 
        currentUser={userData} 
        activeRole={userData?.role_name}
        error={roleError}
        handleSwitchRole={handleSwitchRole}
        handleLogout={handleLogout}
        menuItems={menuItems}
      />
      
      <main className="dashboard-main-content">
        <div className="profile-page-container">
          <div className="profile-header-card">
            <div className="profile-cover"></div>
            <div className="profile-header-content">
              <div className="profile-avatar-large">
                {profilePhotoUrl ? (
                  <img src={profilePhotoUrl} alt="Profile" />
                ) : (
                  <div className="avatar-placeholder-large">&#128100;</div>
                )}
              </div>
              <div className="profile-header-info">
                <h1>{userData?.first_name} {userData?.last_name}</h1>
                <p className="profile-designation">{userData?.designation || 'Employee'}</p>
                <p className="profile-employee-code">Employee ID: {userData?.employee_code || 'N/A'}</p>
              </div>
              {/* <button className="query-btn" onClick={handleQueryClick}>
                <span>&#128172;</span> Query
              </button> */}
            </div>
          </div>

          <div className={`profile-content ${isContentBlurred ? 'blur' : ''}`}>
            <div className="profile-section-tabs">
              <div className="section-tab-header">
                <span className="section-tab-icon">&#128100;</span>
                <h3>Select Details</h3>
              </div>
              <select 
                id="sectionSelect" 
                value={activeSection} 
                onChange={handleSectionChange} 
                className="profile-section-select"
              >
                <option value="official">Official Details</option>
                <option value="personal">Personal Details</option>
              </select>
            </div>

            {activeSection === 'official' && (
              <div className="profile-details-card">
                <div className="card-header">
                  <span className="section-icon">&#127970;</span>
                  <h3>Official Details</h3>
                </div>
                <div className="profile-details-grid">
                  <DetailRow label="Employee Code" value={userData?.employee_code} />
                  <DetailRow label="Employee Name" value={`${userData?.first_name} ${userData?.last_name}`} />
                  <DetailRow label="Work Location" value={officeBranch} />
                  <DetailRow label="Personal Email ID" value={userData?.personal_email_id} />
                  <DetailRow label="Official Email ID" value={userData?.official_email_id} />
                  <DetailRow label="Designation" value={userData?.designation} />
                  <DetailRow label="Department" value={userData?.department} />
                  <DetailRow label="Marital Status" value={userData?.marital_status} />
                </div>
              </div>
            )}

            {activeSection === 'personal' && (
              <div className="profile-details-card">
                <div className="card-header">
                  <span className="section-icon">&#128100;</span>
                  <h3>Personal Details</h3>
                </div>
                <div className="profile-details-grid">
                  <DetailRow label="Contact Number" value={userData?.contact_number} />
                  <DetailRow label="Blood Group" value={userData?.blood_group} />
                  <DetailRow label="Date of Birth" value={userData?.date_of_birth} />
                  <DetailRow label="Work Anniversary" value={userData?.work_anniversary} />
                  <DetailRow label="Nationality" value={userData?.nationality} />
                  <DetailRow label="Religion" value={userData?.religion} />
                  <DetailRow label="Gender" value={userData?.gender} />
                  <DetailRow label="Mailing Address" value={userData?.mailing_address} />
                  <DetailRow label="Permanent Address" value={userData?.permanent_address} />
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Query Popup */}
      {isPopupVisible && (
        <div className="query-popup-overlay" onClick={handlePopupClose}>
          <div className="query-popup-content" onClick={(e) => e.stopPropagation()}>
            <div className="query-popup-header">
              <span className="popup-icon">&#128172;</span>
              <h3>Send a Message</h3>
              <button className="popup-close" onClick={handlePopupClose}>&times;</button>
            </div>
            <div className="query-popup-body">
              {error && (
                <div className="form-error-message">
                  <span>!</span> {error}
                </div>
              )}
              {success && (
                <div className="form-success-message">
                  <span>&#10003;</span> {success}
                </div>
              )}
              <textarea 
                value={message} 
                onChange={(e) => setMessage(e.target.value)} 
                placeholder="Enter your query..."
                className="query-textarea"
              />
              <div className="query-popup-actions">
                <button 
                  className="submit-btn" 
                  onClick={handleSendMessage}
                  disabled={isLoading}
                >
                  {isLoading ? <span className="loading-spinner"></span> : 'Send'}
                </button>
                <button className="cancel-btn" onClick={handlePopupClose}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ID Card Popup */}
      {isIdCardPopupVisible && (
        <div className="id-card-overlay" onClick={() => setIsIdCardPopupVisible(false)}>
          <div className="id-card-popup" onClick={(e) => e.stopPropagation()}>
            <button className="id-card-close" onClick={() => setIsIdCardPopupVisible(false)}>&times;</button>
            
            <div className="id-card-header">
              <img src="./Teamhalt.png" alt="Logo" className="id-card-logo" />
              <span>TEAMHALT PRIVATE LIMITED</span>
            </div>
            
            <div className="id-card-body">
              <div className="id-card-profile">
                <img
                  src={profilePhotoUrl || '/profile.jpg'}
                  alt="Profile"
                  className="id-card-photo"
                />
                <div className="id-card-info">
                  <h4>{userData?.first_name} {userData?.last_name}</h4>
                  <p className="id-designation">{userData?.designation}</p>
                  <div className="id-dob">
                    <strong>DOB:</strong>
                    <span>
                      {userData?.date_of_birth
                        ? new Date(userData.date_of_birth).toLocaleDateString("en-GB").replace(/\//g, "-")
                        : "N/A"}
                    </span>
                  </div>
                </div>
              </div>
              
              <hr className="id-card-divider" />
              
              <div className="id-card-details">
                {[
                  { label: "Emp ID", value: userData?.employee_code },
                  { label: "Department", value: userData?.department },
                  { label: "ESIC No.", value: userData?.esic_no },
                  { label: "UAN No.", value: userData?.uan_no },
                  { label: "Gender", value: userData?.gender },
                  { label: "Contact No.", value: userData?.contact_number },
                ].map((item, idx) => (
                  <div key={idx} className="detail-row">
                    <strong>{item.label}:</strong>
                    <span>{item.value || "N/A"}</span>
                  </div>
                ))}
              </div>
              
              {typeof userData?.work_location_name === "string" &&
                userData.work_location_name.toLowerCase() !== "noida" && (
                <div className="id-card-duty">
                  <strong>On Contractual Duty Of:</strong>
                  <span>{userData?.duty_of || "N/A"}</span>
                </div>
              )}
              
              <div className="id-card-footer">
                https://teamhalt.com/
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;


